<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { FocusTrap } from 'focus-trap-vue'

interface MenuItem {
  ID: number
  title: string
  url: string
  children?: MenuItem[]
}

const props = withDefaults(
  defineProps<{
    closeLabel?: string
    dialogId?: string
    initialItem?: MenuItem | null
    label: string
    menuItems?: MenuItem[]
    menuItemsJson?: string
    secondaryPanelId?: string
    srBackLabel?: string
    srCloseLabel?: string
    srExpandLabel?: string
  }>(),
  {
    closeLabel: 'Close',
    dialogId: 'menu-modal',
    initialItem: null,
    label: 'Menu Modal',
    menuItems: () => [],
    secondaryPanelId: 'menu-modal-secondary-panel',
    srBackLabel: 'Back to main menu',
    srCloseLabel: 'Close menu',
    srExpandLabel: 'Show submenu for'
  }
)

const emit = defineEmits<{
  (e: 'close'): void
}>()

// The menu tree is passed as a plain JSON string (menu-items-json) instead of a
// bound :menu-items expression so TranslatePress can't rewrite the titles inside
// the attribute value and crash Vue on /en/ pages. Fall back to the menuItems prop.
const items = computed<MenuItem[]>(() => {
  if (props.menuItemsJson) {
    try {
      return JSON.parse(props.menuItemsJson) as MenuItem[]
    } catch {
      return []
    }
  }
  return props.menuItems ?? []
})

const activeItem = ref<MenuItem | null>(null)
const expandedChild = ref<number | null>(null)
const isSecondaryOpen = ref(false)

const handleItemClick = (item: MenuItem) => {
  if (item.children && item.children.length > 0) {
    activeItem.value = item
    isSecondaryOpen.value = true
  } else {
    window.location.href = item.url
    closeMenu()
  }
}

const toggleChild = (id: number) => {
  expandedChild.value = expandedChild.value === id ? null : id
}

const closeSecondary = () => {
  activeItem.value = null
  isSecondaryOpen.value = false
  expandedChild.value = null
}

const closeMenu = () => {
  emit('close')
}

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  if (props.initialItem) {
    activeItem.value = props.initialItem
    isSecondaryOpen.value = true
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

const firstLevelRef = ref<HTMLElement | null>(null)
const secondaryLevelRef = ref<HTMLElement | null>(null)

const firstLevelFallback = computed(() => {
  return () => firstLevelRef.value?.querySelector('button') as HTMLElement
})

const secondaryLevelFallback = computed(() => {
  return () => secondaryLevelRef.value?.querySelector('button') as HTMLElement
})

defineExpose({ handleItemClick })

const getChildPanelId = (id: number) => {
  return `${props.secondaryPanelId}-child-${id}`
}
</script>

<template>
  <FocusTrap
    :active="true"
    :initial-focus="isSecondaryOpen ? secondaryLevelFallback : firstLevelFallback"
  >
    <div
      :id="props.dialogId"
      role="dialog"
      :aria-label="props.label"
      aria-modal="true"
      class="absolute left-0 top-0 w-full md:max-w-[550px] h-full bg-dark-green z-2 overflow-hidden"
      @click.stop
    >
      <div
        ref="firstLevelRef"
        class="first-level transition-transform duration-300"
        :style="{ transform: isSecondaryOpen ? 'translateX(-100%)' : 'translateX(0)' }"
      >
        <div class="max-md:hidden flex justify-end px-4 py-4">
          <button
            class="cursor-pointer flex items-center gap-x-1"
            :tabindex="isSecondaryOpen ? -1 : 0"
            :aria-label="props.srCloseLabel"
            @click="closeMenu"
          >
            <p class="body-3-semibold text-white">{{ props.closeLabel }}</p>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 20 16"
              fill="none"
            >
              <rect
                width="20"
                height="2"
                x="2.222"
                y="14.142"
                fill="#ffffff"
                rx="1"
                transform="rotate(-45 2.222 14.142)"
              />
              <rect
                width="20"
                height="2"
                x="3.636"
                y=".444"
                fill="#ffffff"
                rx="1"
                transform="rotate(45 3.636 .444)"
              />
            </svg>
          </button>
        </div>

        <ul>
          <li v-for="item in items" :key="item.ID" class="text-white p-4 body-2-semibold">
            <button
              v-if="item.children && item.children.length > 0"
              class="flex justify-between w-full cursor-pointer items-center"
              :tabindex="isSecondaryOpen ? -1 : 0"
              :aria-controls="props.secondaryPanelId"
              :aria-expanded="isSecondaryOpen && activeItem?.ID === item.ID"
              :aria-label="props.srExpandLabel + ' ' + item.title"
              @click="handleItemClick(item)"
            >
              <span>{{ item.title }}</span>
              <div
                v-if="item.children && item.children.length > 0"
                class="cursor-pointer w-4 h-4 bg-light-green flex items-center justify-center rounded-full"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                >
                  <path
                    d="M1 4.9375L8.3125 4.9375"
                    stroke="#053618"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4.375 8.875L8.3125 4.9375L4.375 1"
                    stroke="#053618"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </button>

            <a
              v-else
              :href="item.url"
              :tabindex="isSecondaryOpen ? -1 : 0"
              class="block underline"
              @click="closeMenu"
            >
              {{ item.title }}
            </a>
          </li>
        </ul>
        <button
          :tabindex="isSecondaryOpen ? -1 : 0"
          class="md:hidden sr-only focus:not-sr-only focus:py-1 focus:px-2 focus:bg-white focus:text-dark-green focus:rounded-[2px] body-3-semibold focus:ml-4 focus:mt-2"
          @click="closeMenu"
        >
          {{ props.srCloseLabel }}
        </button>
      </div>

      <div
        :id="props.secondaryPanelId"
        ref="secondaryLevelRef"
        class="secondary-level py-4 h-full w-full bg-dark-green absolute left-0 top-0 transition-transform duration-300 z-3 overflow-auto"
        :aria-hidden="!isSecondaryOpen"
        :style="{ transform: isSecondaryOpen ? 'translateX(0)' : 'translateX(100%)' }"
      >
        <div class="flex justify-between items-center px-4">
          <button
            :tabindex="isSecondaryOpen ? 0 : -1"
            :aria-label="props.srBackLabel"
            class="flex items-center gap-2 mb-4"
            @click="closeSecondary"
          >
            <div
              class="cursor-pointer bg-white rounded-full flex items-center justify-center w-4 h-4 shrink-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <path
                  d="M8.3125 4.9375L1 4.9375"
                  stroke="#053618"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M4.9375 1L1 4.9375L4.9375 8.875"
                  stroke="#053618"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </button>

          <button
            class="max-md:hidden cursor-pointer flex items-center gap-x-1"
            :tabindex="isSecondaryOpen ? 0 : -1"
            :aria-label="props.srCloseLabel"
            @click="closeMenu"
          >
            <p class="body-3-semibold text-white">{{ props.closeLabel }}</p>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 20 16"
              fill="none"
            >
              <rect
                width="20"
                height="2"
                x="2.222"
                y="14.142"
                fill="#ffffff"
                rx="1"
                transform="rotate(-45 2.222 14.142)"
              />
              <rect
                width="20"
                height="2"
                x="3.636"
                y=".444"
                fill="#ffffff"
                rx="1"
                transform="rotate(45 3.636 .444)"
              />
            </svg>
          </button>
        </div>

        <ul v-if="activeItem">
          <li class="text-white pt-4 body-2-semibold">
            <a
              :href="activeItem.url"
              :tabindex="isSecondaryOpen ? 0 : -1"
              class="block px-4 pb-4 underline text-light-green"
              @click="closeMenu"
              >{{ activeItem.title }}</a
            >
          </li>
          <li
            v-for="child in activeItem.children"
            :key="child.ID"
            class="text-white pt-4 body-2-semibold pb-4"
          >
            <div
              v-if="child.children && child.children.length > 0"
              class="flex justify-between items-center w-full px-4 pb-4"
            >
              <a
                :href="child.url"
                :tabindex="isSecondaryOpen ? 0 : -1"
                class="underline"
                @click="closeMenu"
                >{{ child.title }}</a
              >
              <button
                :tabindex="isSecondaryOpen ? 0 : -1"
                :aria-expanded="expandedChild === child.ID"
                :aria-controls="getChildPanelId(child.ID)"
                :aria-label="`${props.srExpandLabel} ${child.title}`"
                class="shrink-0"
                @click="toggleChild(child.ID)"
              >
                <div
                  v-if="expandedChild === child.ID"
                  class="cursor-pointer w-4 h-4 flex items-center justify-center rounded-full bg-light-green"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="11"
                    viewBox="0 0 12 11"
                    fill="none"
                  >
                    <path
                      d="M1 5L11 5"
                      stroke="#053618"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div
                  v-else
                  class="cursor-pointer w-4 h-4 flex items-center justify-center rounded-full bg-light-green"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M1 6L11 6"
                      stroke="#053618"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M6 1L6 11"
                      stroke="#053618"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </button>
            </div>
            <a
              v-else
              :href="child.url"
              :tabindex="isSecondaryOpen ? 0 : -1"
              class="block px-4 underline"
              @click="closeMenu"
              >{{ child.title }}</a
            >

            <ul
              v-if="child.children"
              :id="getChildPanelId(child.ID)"
              :class="['overflow-hidden bg-green border-l border-light-green']"
              :aria-expanded="expandedChild === child.ID"
              :inert="expandedChild !== child.ID"
              :style="{
                maxHeight: expandedChild === child.ID ? child.children.length * 80 + 'px' : '0px',
                transition: 'max-height 0.3s cubic-bezier(0.65, 0, 0.35, 1)'
              }"
            >
              <li
                v-for="grandchild in child.children"
                :key="grandchild.ID"
                class="text-white body-2-semibold"
                :class="grandchild.url !== '#' ? 'py-4' : ''"
              >
                <div v-if="grandchild.url === '#'" class="border-t border-light-green w-full" />
                <a
                  v-else
                  :href="grandchild.url"
                  class="px-4 underline"
                  :tabindex="expandedChild === child.ID ? 0 : -1"
                  @click="closeMenu"
                  >{{ grandchild.title }}</a
                >
              </li>
            </ul>
          </li>
        </ul>

        <button
          :tabindex="isSecondaryOpen ? 0 : -1"
          class="md:hidden sr-only focus:not-sr-only focus:py-1 focus:px-2 focus:bg-white focus:text-dark-green focus:rounded-[2px] body-3-semibold focus:ml-4 focus:mt-2"
          @click="closeMenu"
        >
          {{ props.srCloseLabel }}
        </button>
      </div>
    </div>
  </FocusTrap>
</template>
